/**
 * AI Writing Assistant API – streaming generation.
 * Calls POST /api/v1/ai-writing-assistant/generate, consumes the stream (NDJSON or SSE),
 * calls onChunk(accumulatedText) for each delta, resolves with full text when done.
 */

import { apiUrl } from 'services/apiBaseUrl';
import { getActiveAuthHeaders } from 'utils/getActiveAuthHeaders';

const GENERATE_URL = apiUrl('/v1/ai-writing-assistant/generate');

function getAuthHeaders() {
  return {
    'Content-Type': 'application/json',
    ...getActiveAuthHeaders(),
  };
}

/** Collapse 3+ consecutive newlines to a single blank line (\n\n). */
function normalizeNewlines(text) {
  return text.replace(/\n{3,}/g, '\n\n');
}

/**
 * Consume SSE or NDJSON stream.
 * - SSE: events like "event: message\ndata: {\"text\":\"...\"}\n\n"; we parse data lines and extract .text.
 * - NDJSON: one JSON object per line with optional "text" field.
 * Calls onChunk(accumulatedText) for each delta; resolves with full accumulated text.
 * Normalizes excessive blank lines (3+ newlines → \n\n) so the composer shows at most one blank line between paragraphs.
 */
async function consumeStream(reader, onChunk) {
  const decoder = new TextDecoder();
  let buffer = '';
  let accumulated = '';
  const processLine = line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let jsonStr = trimmed;
    if (trimmed.startsWith('data:')) {
      jsonStr = trimmed.slice(5).trim();
    }
    try {
      const obj = JSON.parse(jsonStr);
      if (typeof obj.text === 'string') {
        accumulated += obj.text;
        onChunk(normalizeNewlines(accumulated));
      }
    } catch {
      // ignore malformed lines
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n');
    buffer = parts.pop() ?? '';
    for (const line of parts) {
      processLine(line);
    }
  }
  if (buffer.trim()) {
    buffer.split('\n').forEach(processLine);
  }
  return normalizeNewlines(accumulated);
}

/**
 * Generate message with streaming.
 * @param {{ keyPoints: string, tone: string, language: string }} params
 * @param {{ onChunk: (accumulatedText: string) => void }} options
 * @returns {Promise<string>} Full generated text when stream ends.
 */
export async function generateMessageStream(params, { onChunk }) {
  const response = await fetch(GENERATE_URL, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      keyPoints: params.keyPoints,
      tone: params.tone,
      language: params.language,
    }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('AiWritingAssistant.errors.forbidden');
    }
    if (response.status >= 502 && response.status < 504) {
      throw new Error('AiWritingAssistant.errors.serviceUnavailable');
    }
    const text = await response.text();
    throw new Error(text || `AiWritingAssistant.errors.requestFailed`);
  }

  const body = response.body;
  if (!body) {
    throw new Error('AiWritingAssistant.errors.noBody');
  }

  const reader = body.getReader();
  const fullText = await consumeStream(reader, onChunk);
  return fullText;
}
